import React, { useState } from 'react';
import { Card, CardHeader, Box, Divider, Grid, Button } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import {
  FilterChip,
  QuickSearch,
  useAPI,
  VerticalBoxContainer,
  PageContentWrapper,
  DeleteModal,
} from '@shared/components';
import ExcelExportButton from '../../../../components/ExcelExportButton';
import useGlobalAlertContext from '../../../../hooks/useGlobalAlertContext';
import axios from '../../../../utils/axios';
import getChipValue from '../../../../utils/getChipValue';
import { handleChipDelete } from '../../../../utils/filterHelpers';
import { handleSortChange, getSortDirection } from '../../../../utils/sortHelpers';
import useTheme from '../../../../hooks/useTheme';
import Modal from './Modal';
import List from './Table';
import Filter from './Filter';

const tableColumns = [
  {
    name: 'Naziv',
    value: 'name',
    align: 'left',
    width: 150,
    required: true,
    placeholder: 'nazivu',
  },
  {
    name: 'Datum kreiranja',
    value: 'timeCreated',
    align: 'left',
    required: true,
    width: 150,
    placeholder: 'datumu kreiranja',
  },
  {
    name: 'Ekstenzija',
    value: 'extension',
    align: 'left',
    required: true,
    width: 150,
    placeholder: 'ekstenziji',
  },
  {
    name: 'Veličina fajla',
    value: 'fileSize',
    align: 'left',
    required: true,
    width: 150,
    placeholder: 'velićini fajla',
  },
  {
    name: 'Opis',
    value: 'description',
    align: 'left',
    required: true,
    width: 150,
    placeholder: 'opisu',
  },
];

const exportFields = [
  {
    label: 'Naziv',
    value: 'name',
  },
  {
    label: 'Prilog',
    value: 'attachment',
  },
  {
    label: 'Datum kreiranja',
    value: 'timeCreated',
    type: 'dateTime',
  },
  {
    label: 'Ekstenzija',
    value: 'extension',
  },
  {
    label: 'Veličina fajla',
    value: 'fileSize',
  },
  {
    label: 'Opis',
    value: 'description',
  },
];

const getLabelText = (value) => {
  const map = {
    userCreated: 'Kreirao',
    description: 'Opis',
    timeCreated: 'Datum kreiranja',
  };
  return map[value];
};

const DocumentAttachments = ({ parentDataId }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({});
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sorters, setSorters] = useState([]);
  const [selectedDocumentAttachment, setSelectedDocumentAttachment] = useState(undefined);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();
  const [triggerRerender, setRerender] = useState(false);

  const [documentAttachments = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`document-attachments`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: { ...filters, documentId: parentDataId },
            orderBy: [...sorters, '"id" desc'],
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        setFilters={setFilters}
        setPage={setPage}
        getLabelText={getLabelText}
      />
      <Modal
        visible={modalVisible}
        setVisible={setModalVisible}
        selectedDocumentAttachment={selectedDocumentAttachment}
        setSelectedDocumentAttachment={setSelectedDocumentAttachment}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        resetSelectedDocument={() => {
          setSelectedDocumentAttachment(undefined);
        }}
        setRerender={setRerender}
        parentDataId={parentDataId}
      />
      <DeleteModal
        dataLength={documentAttachments.totalCount}
        page={page}
        setPage={setPage}
        path="documentAttachments"
        visible={deleteModalVisible}
        setRerender={setRerender}
        title={`Obriši prilog dokumenta: ${selectedDocumentAttachment?.id}`}
        promptText={`Da li ste sigurni da želite obrisati prilog dokumenta ${selectedDocumentAttachment?.id}?`}
        buttonText="Obriši prilog dokumenta"
        message="Uspješno obrisan prilog dokumenta"
        setVisible={setDeleteModalVisible}
        resetObject={() => setSelectedDocumentAttachment(undefined)}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        objectId={selectedDocumentAttachment?.id}
        axios={axios}
        theme={theme}
      />
      <PageContentWrapper style={{ paddingTop: '4px', paddingRight: '2px', paddingLeft: '2px' }}>
        <Card>
          <CardHeader
            title={
              <QuickSearch
                name="description"
                placeholder="Brza pretraga po opisu"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={documentAttachments.totalCount}
                    sortById={false}
                    variant="outlined"
                    fields={exportFields}
                    sorters={[...sorters, '"id" desc']}
                    filters={{ ...filters, documentId: parentDataId }}
                    routeName="document-attachments"
                    fileName={`${parentDataId}-prilozi-dokumenta`}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => setModalVisible(true)}
                  >
                    <Box fontSize="small">Novi prilog dokumenta</Box>
                  </Button>
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <List
            loading={loading}
            error={error}
            documentAttachments={documentAttachments.nodes}
            tableColumns={tableColumns}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={documentAttachments.totalCount}
            handleSortChange={handleSortChange}
            getSortDirection={getSortDirection}
            setSorters={setSorters}
            sorters={sorters}
            setDeleteModalVisible={setDeleteModalVisible}
            setSelectedDocumentAttachment={setSelectedDocumentAttachment}
            selectedDocumentAttachment={selectedDocumentAttachment}
            setModalVisible={setModalVisible}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default DocumentAttachments;
